import {
  ChakraBox,
  ChakraFlex,
  EmptySectionCard,
  FAIconName,
  GroupListViewCardV2,
  InfiniteScroll,
  SkeletonLoader,
  useToastMessage,
} from '@equip.health/ui';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  memo,
  useMemo,
} from 'react';
import { GroupsListTypeOptions } from '~/components/common/calendar/schedule/ScheduleChangerV2';
import {
  GET_GROUP_CLASS_PAGE_SIZE_V2,
  GroupTypeTagColorMapper,
} from '~/lib/constants/groupClasses.constants';
import { useLazyGetActiveOrArchiveGroupsV2Query } from '~/store/services/groupClass';
import { GROUPS_LIST_CONSTANTS } from './groupsList.constants';
import { mergeGroupsWithCache } from './groupsList.utils';
import GroupClassDetailsDrawer from './GroupClassDetailsView';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface GroupsListProps {
  listType: GroupClassListType;
  groupClassTitle: string;
  totalCount: number;
  isNewGroupCreated: boolean;
  resetNewGroupCreated: () => void;
}

/** Renders individual group cards with optimized memoization */
const GroupCardItem = memo(
  ({
    ageRanges,
    attendeeRoles,
    groupStatusCode,
    groupType,
    groupTypeCode,
    id,
    groupActiveSectionCount,
    iconName,
    isArchived,
    onResetPage,
    isSensitive,
    title,
    treatmentModalities,
    treatmentPrograms,
  }: EnrichedGroupList & { isArchived: boolean; onResetPage: () => void }) => {
    const { trackEvent } = useAnalytics();
    const [openGroupDetailsDrawer, setOpenGroupDetailsDrawer] =
      useState<boolean>(false);

    return (
      <>
        <GroupListViewCardV2
          key={id}
          id={id}
          title={title}
          iconName={iconName}
          ageRange={ageRanges}
          attendeeRoles={attendeeRoles}
          groupType={groupType}
          groupTypeTagColor={GroupTypeTagColorMapper[groupTypeCode]}
          isArchived={isArchived}
          isDraft={groupStatusCode === 'DRAFT'}
          isSensitive={isSensitive}
          sectionsCount={groupActiveSectionCount}
          modalities={treatmentModalities}
          programs={treatmentPrograms}
          onCardClick={() => {
            setOpenGroupDetailsDrawer(true);
            trackEvent(GroupSectionEvents.groupListCardClicked, {
              archivedGroup: isArchived,
              Source: isArchived
                ? AnalyticsSourceValues.ARCHIVE_SUBTAB
                : AnalyticsSourceValues.ALL_GROUPS_SUBTAB,
              'Group title': title,
              'Group status': groupStatusCode,
            });
            trackEvent(GroupSectionEvents.groupClassDetailsDrawerViewed, {
              Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
            });
          }}
        />
        {openGroupDetailsDrawer && (
          <GroupClassDetailsDrawer
            groupClassId={id}
            isOpen={openGroupDetailsDrawer}
            onClose={() => setOpenGroupDetailsDrawer(false)}
            handleSuccess={onResetPage}
          />
        )}
      </>
    );
  },
);

GroupCardItem.displayName = 'GroupCardItem';

const RenderGroupsList = ({
  listType,
  groupClassTitle,
  totalCount,
  isNewGroupCreated,
  resetNewGroupCreated,
}: GroupsListProps) => {
  const { createToast } = useToastMessage();

  const isSkipped = useMemo(
    () =>
      listType !== GroupsListTypeOptions.ALL_GROUPS &&
      listType !== GroupsListTypeOptions.ARCHIVE,
    [listType],
  );

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [groupsList, setGroupsList] = useState<EnrichedGroupList[]>([]);
  const [isFetching, setIsFetching] = useState(!isSkipped);
  const errorToastShownRef = useRef(false);

  const [fetchGroups] = useLazyGetActiveOrArchiveGroupsV2Query();

  useLayoutEffect(() => {
    setGroupsList([]);
    setPageNumber(1);
    setIsFetching(!isSkipped);
    errorToastShownRef.current = false;
  }, [listType, groupClassTitle, isSkipped]);

  const showErrorToast = () => {
    if (!errorToastShownRef.current) {
      errorToastShownRef.current = true;
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: GROUPS_LIST_CONSTANTS.ERROR_TOAST_ID,
        text: GROUPS_LIST_CONSTANTS.ERROR_MESSAGE,
        variant: 'critical',
      });
    }
  };

  const fetchPage = useCallback(
    (page: number) => {
      if (isSkipped) return;
      setIsFetching(true);
      fetchGroups({
        pageNumber: page,
        isActive: listType === GroupsListTypeOptions.ALL_GROUPS,
        title: groupClassTitle || undefined,
      })
        .then((result) => {
          if (result.error) {
            showErrorToast();
          } else if (result.data && result.data.length > 0) {
            setGroupsList((prev) => mergeGroupsWithCache(prev, result.data!));
          }
        })
        .finally(() => {
          setIsFetching(false);
        });
    },
    [listType, groupClassTitle, isSkipped],
  );

  useEffect(() => {
    fetchPage(pageNumber);
  }, [pageNumber, fetchPage]);

  const resetGroupsList = () => {
    setGroupsList([]);
    errorToastShownRef.current = false;
    if (pageNumber === 1) {
      fetchPage(1);
    } else {
      setPageNumber(1);
    }
  };

  useEffect(() => {
    if (isNewGroupCreated) {
      resetNewGroupCreated();
      resetGroupsList();
    }
  }, [isNewGroupCreated, resetNewGroupCreated]);

  const itemsFetchedSoFar = pageNumber * GET_GROUP_CLASS_PAGE_SIZE_V2;
  const hasMore = itemsFetchedSoFar < totalCount;

  return (
    <ChakraBox
      height="100%"
      id={GROUPS_LIST_CONSTANTS.TOP_LEVEL_ID}
      overflow="auto"
    >
      <ChakraFlex direction="column" marginRight="24px" paddingBottom="36px">
        <SkeletonLoader
          id={`${GROUPS_LIST_CONSTANTS.TOP_LEVEL_ID}__skeleton-loader`}
          isLoaded={!isFetching || groupsList.length > 0}
        >
          {groupsList.length > 0 ? (
            <InfiniteScroll
              dataLength={groupsList.length}
              fetchData={() =>
                !isFetching && hasMore && setPageNumber((p) => p + 1)
              }
              hideCount
              id={`${GROUPS_LIST_CONSTANTS.TOP_LEVEL_ID}__infinite-scroll`}
              scrollableTarget={GROUPS_LIST_CONSTANTS.TOP_LEVEL_ID}
              showLoader={isFetching}
              style={{ overflow: 'hidden' }}
              totalCount={totalCount}
            >
              <ChakraFlex direction="column" gap={2}>
                {groupsList.map((group) => (
                  <GroupCardItem
                    key={group.id}
                    {...group}
                    isArchived={listType === GroupsListTypeOptions.ARCHIVE}
                    onResetPage={resetGroupsList}
                  />
                ))}
              </ChakraFlex>
            </InfiniteScroll>
          ) : (
            <EmptySectionCard
              caption={GROUPS_LIST_CONSTANTS.EMPTY_STATE_MESSAGE}
              id={`${GROUPS_LIST_CONSTANTS.TOP_LEVEL_ID}__empty`}
            />
          )}
        </SkeletonLoader>
      </ChakraFlex>
    </ChakraBox>
  );
};

export default RenderGroupsList;
